import { DynamoDBClient, QueryCommand } from "@aws-sdk/client-dynamodb";
import { unmarshall } from "@aws-sdk/util-dynamodb";
import { useQuery } from "@tanstack/vue-query";
import { type ComputedRef, computed } from "vue";

export interface Sector {
	sector_id: string;
	sector_name: string;
	project_id: string;
	event_id: string;
	created_at: string;
	updated_at: string;
}

/**
 * Get all sectors for an event.
 * Sectors live under PK = PROJECT#{project_id}#EVENT#{event_id}, SK begins_with SECTOR#
 */
export async function getEventSectors(
	projectId: string,
	eventId: string,
	dynamodb: {
		user_table_name: string;
		client: DynamoDBClient;
	},
): Promise<Sector[]> {
	const { client, user_table_name } = dynamodb;
	const sectors: Sector[] = [];

	const command = new QueryCommand({
		TableName: user_table_name,
		KeyConditionExpression: "PK = :pk AND begins_with(SK, :skPrefix)",
		ExpressionAttributeValues: {
			":pk": { S: `PROJECT#${projectId}#EVENT#${eventId}` },
			":skPrefix": { S: "SECTOR#" },
		},
	});

	const result = await client.send(command);
	if (result.Items) {
		for (const item of result.Items) {
			const u = unmarshall(item);
			const sk = u.SK as string;
			const sectorIdMatch = sk.match(/SECTOR#(.+)$/);
			const sectorId = sectorIdMatch ? sectorIdMatch[1] : "";

			if (sectorId) {
				sectors.push({
					sector_id: sectorId,
					sector_name: (u.sector_name as string) || sectorId,
					project_id: (u.project_id as string) || projectId,
					event_id: (u.event_id as string) || eventId,
					created_at: (u.created_at as string) || "",
					updated_at: (u.updated_at as string) || "",
				});
			}
		}
	}

	return sectors.sort((a, b) => a.sector_name.localeCompare(b.sector_name));
}

export function useEventSectors(
	projectId: ComputedRef<string | null | undefined>,
	eventId: ComputedRef<string | null | undefined>,
	dynamodb: ComputedRef<{
		user_table_name: string;
		client: DynamoDBClient;
	} | null>,
	credentialsReset: ComputedRef<number>,
) {
	const enabled = computed(
		() => !!projectId.value && !!eventId.value && !!dynamodb.value,
	);

	return useQuery({
		queryKey: ["event-sectors", projectId, eventId, credentialsReset],
		queryFn: ({ queryKey: [_, pId, eId] }) => {
			const db = dynamodb.value;
			if (!db?.client) {
				throw new Error("DynamoDB client not initialized");
			}
			return getEventSectors(pId as string, eId as string, {
				user_table_name: db.user_table_name,
				client: db.client,
			});
		},
		enabled,
	});
}
